/**
 * Manpower P&L — Vehicles API service (vendor↔vehicle mapping).
 *
 * Types come from the OpenAPI-generated client (src/api/generated/manpower-vehicles.ts),
 * NOT hand-written. The response envelope is always { status, statusCode, message, data }
 * and the JWT + city scoping are handled by the shared axios client (auth interceptor in api.ts).
 *
 * NOTE: the list is PER-VENDOR — every list call needs a vendorId.
 */
import { apiService } from './api';
import type { components } from '../api/generated/manpower-vehicles';

// Single source of truth for the vehicle shapes — straight from the contract.
export type Vehicle = components['schemas']['Vehicle'];
export type AddVehicleBody = components['schemas']['AddVehicleBody'];
export type UpdateVehicleBody = components['schemas']['UpdateVehicleBody'];

/** The list response: the standard envelope + a typed `data` array. */
export interface ListVehiclesResponse {
	status: boolean;
	statusCode: number;
	message: string;
	data: Vehicle[];
}

export class ManpowerVehiclesApiService {
	/**
	 * GET /v1/api/manpower/vendors/{vendorId}/vehicles — list active vehicles for a vendor.
	 * baseURL already ends in /v1/api, so the relative path is /manpower/vendors/{vendorId}/vehicles.
	 */
	static async listVehicles(vendorId: number): Promise<ListVehiclesResponse> {
		const response = await apiService.get<ListVehiclesResponse>(
			`/manpower/vendors/${vendorId}/vehicles`
		);
		// apiService.get already returns response.data from axios, so this IS the envelope.
		return response as unknown as ListVehiclesResponse;
	}

	/** POST /vendors/{vendorId}/vehicles — add a vehicle. Returns the new id. */
	static async addVehicle(vendorId: number, body: AddVehicleBody): Promise<{ id?: number }> {
		const response = await apiService.post<{ data: { id?: number } }>(
			`/manpower/vendors/${vendorId}/vehicles`,
			body
		);
		return (response as unknown as { data?: { id?: number } }).data ?? {};
	}

	/** PATCH /vehicles/{vehicleId} — update type / facility / status. */
	static async updateVehicle(vehicleId: number, body: UpdateVehicleBody): Promise<void> {
		await apiService.patch(`/manpower/vehicles/${vehicleId}`, body);
	}

	/** DELETE /vehicles/{vehicleId} — soft-delete (sets status=inactive). */
	static async deleteVehicle(vehicleId: number): Promise<void> {
		await apiService.delete(`/manpower/vehicles/${vehicleId}`);
	}
}
